import type { User } from 'oidc-client-ts';
import { createContext, use } from 'react';
import { userManager } from './userManager.ts';

/** `signing-out`: on the way to Keycloak's logout; guards must not start a new sign-in meanwhile. */
export type AuthStatus = 'loading' | 'anonymous' | 'authenticated' | 'signing-out';

export interface AuthContextValue {
  status: AuthStatus;
  user: User | null;
  signIn: (returnTo?: string) => Promise<void>;
  /** Opens Keycloak's registration form. */
  signUp: (returnTo?: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** A valid access token, refreshed when it is about to expire; null when signed out. */
  getAccessToken: () => Promise<string | null>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export const EXPIRY_MARGIN_SECONDS = 30;

let renewal: Promise<User | null> | null = null;
/** Single-flight token renewal shared by every caller. */
export function renew(): Promise<User | null> {
  renewal ??= userManager
    .signinSilent()
    .catch(async () => {
      await userManager.removeUser();
      return null;
    })
    .finally(() => {
      renewal = null;
    });
  return renewal;
}

export function useAuth(): AuthContextValue {
  const value = use(AuthContext);
  if (!value) throw new Error('useAuth must be used inside <AuthProvider>');
  return value;
}
